import { Report } from '@/api/ReportController';
import { Queue, QueueResponse, Role, User, UserContext } from '../../api/CamundaController';
import { RolesData } from '@/interface';
import { getRoles } from '@/core/helpers/PageHelpers';

const LOGIN_SUCCESS = 'Header/Login/LOGIN_SUCCESS';
const LOGIN_FAILURE = 'Header/Login/LOGIN_FAILURE';
const LOGOUT = 'Header/Login/LOGOUT';
const UPDATE_USER_PHOTO = 'Header/User/UPDATE_USER_PHOTO';
const UPDATE_USER_ROLES = 'Header/User/UPDATE_USER_ROLES';
const OPEN_MENUBAR = 'Header/Menu/OPEN_MENUBAR';
const SELECT_MENU = 'Header/Menu/SELECT_MENU';
const SELECT_REPORT = 'Header/Report/SELECT_REPORT';
const OPEN_QUEUE_MENUBAR = 'Header/Menu/OPEN_QUEUE_MENUBAR';
const SELECT_QUEUE_MENU = 'Header/Menu/SELECT_QUEUE_MENU';
const UPDATE_QUEUE_MENU = 'Header/Menu/UPDATE_QUEUE_MENU';
const UPDATE_USER_QUEUE = 'Header/User/UPDATE_USER_QUEUE';
const UPDATE_USER = 'Header/User/UPDATE_USER';
const SELECT_ACTIVE_QUEUE = 'Header/Queue/SELECT_ACTIVE_QUEUE';
const UPDATE_RETURN_REQUEST_QUEUE = 'Header/Queue/UPDATE_RETURN_REQUEST_QUEUE';
const UPDATE_CASHIER_REQUEST_QUEUE = 'Header/Queue/UPDATE_CASHIER_REQUEST_QUEUE';
const UPDATE_ACTIVE_DEPARTMENT_ID = 'Header/User/UPDATE_ACTIVE_DEPARTMENT_ID';
const UPDATE_WORK_WITH_KASSA = 'Header/User/UPDATE_WORK_WITH_KASSA';
const UPDATE_CASH_TRANSFER_REQUEST_QUEUE = 'Header/Queue/UPDATE_CASH_TRANSFER_REQUEST_QUEUE';
const UPDATE_CASH_REQUEST_QUEUE = 'Header/Queue/UPDATE_CASH_REQUEST_QUEUE';

export interface SelectedMenu {
    path: string;
    title: string;
    index: number;
}
/**
 * Стор гидрируется пользователем именно отсюда - из ключа userContext, а не из
 * снимка state: токены при refresh обновляет axios и только в этом ключе, так
 * что снимок почти всегда содержит уже протухшие. Битый JSON (обрыв записи,
 * переполнение квоты) не должен ронять весь стор в белый экран.
 */
const readUserContext = (): UserContext => {
    try {
        return JSON.parse(localStorage.getItem('userContext') || '{}');
    } catch {
        return {} as UserContext;
    }
};

const userContext = readUserContext();

export interface RequestQueueItem {
    processInstanceId: string;
    taskId: string;
    'task.businessKey': string;
    login: string;
    date: string;
    buyAmount: string;
    saleAmount: string;
    buyCurrencyCode: string;
    saleCurrencyCode: string;
    businessKey: string;
    phone: string;
    acceptStatus: string;
    requestStatus: string;
    depsId: string;
    currentRate: string;
    isIndividual: "true" | "false";
    actualRate: string;
    afmStatus: string;
    rate2: string;
    overTimeStatus: string;
    clientOperationsCount: string;
    clientOperationsSum: string;
    requestId: string;
    acceptLimit: string;
    kassaId: string;
    updatedRequestStatus: string;
    acceptId: string;
    clientFullName: string;
    userAgent: string;
}

export interface AcceptQueueItem {
    processInstanceId: string;
    taskId: string;
    'task.businessKey': string;
    login: string;
    date: string;
    depId: string;
    acceptType: string;
    statusReturn: string;
    requestId: string;
    returnDate: string;
    actionType: string;
    currencyFrom: string;
    currencyTo: string;
    amount: string;
    value: string;
    rate: string;
    kassaId: string;
    initiator: string;
    status: string;
    isIndividual: "true" | "false";
    docId: string;
    addressId: string;
    firstName: string;
    lastName: string;
    middleName: string;
    iin: string;
    phone: string;
    birthDay: string;
    citizen: string;
    country: string;
    countryCode: string;
    region: string;
    locality: string;
    street: string;
    number: string;
    flat: string;
    docType: string;
    docNumber: string;
    issuePlace: string;
    issueDate: string;
    validDate: string;
    fullName: string;
    currencyAddCode: string;
    currencyAddDescription: string;
    currencyAddRatesBuy: string;
    currencyAddRatesSale: string;
    currencyAddInitiator: string;
    currencyDeleteId: string;
    currencyDeleteCode: string;
    currencyDeleteDescription: string;
    currencyDeleteRatesBuy: string;
    currencyDeleteRatesSale: string;
    currencyDeleteInitiator: string;
}

export interface CashTransferQueueItem {
    processInstanceId: string;
    taskId: string;
    taskBusinessKey: string;
    login: string;
    fromKassaId: string;
    fromKassaName: string;
    toKassaId: string;
    toKassaName: string;
    currencyCode: string;
    amount: string;
    initiator: string;
    initiatorFullName: string;
    token: string;
    fromDepartmentName: string;
    toDepartmentName: string;
    nationalBankRate: string;
    operationType: string;
    fromCashAcceptId: string;
    toCashAcceptId: string;
    fromComment: string;
    toComment: string;
    /** Номера слитков при перемещении золота, через запятую */
    goldSerial: string;
    fromOfdRes: string;
    title: string;
    address: string;
    bin: string;
    serial_number: string;
    fns_kkm_id: string;
    shift_document_number: string;
    fr_shift_number: string;
    value: string,
    fullDate: string,
}

export interface CashRequestQueueItem {
    acceptedBy: string;
    actionType: string; //0 - приход; 1 - снятие; 2 - приход от БВУ; 3 - снятие для БВУ
    agreementId: string;
    amount: string;
    cashAcceptId: string;
    cashId: string;
    comment: string;
    createdBy: string;
    currencyCode: string;
    currencyId: string;
    departmentId: string;
    employeeName: string;
    goldInKzt: string;
    goldRate: string;
    goldSerial: string;
    isMinus: boolean;
    kassaId: string;
    nbRate: string;
    operationType: string;
    paymentType: string;
    processInstanceId: string;
    rate: string;
    status: string;  //0 - создан; 1 - принят; 2 - отклонен; 3 - БВУ сохранен; 4 - БВУ создан; 5 - БВУ принят; 6 - БВУ отклонен;
    "task.businessKey": string;
    taskId: string;
    wacRate: string;
}

export class HeaderReducer {
    userContext?: UserContext = userContext;
    rolesData?: RolesData = getRoles(userContext?.user?.roles || []);
    acceptsQueue?: AcceptQueueItem[] = [];
    cashierRequestQueue?: RequestQueueItem[] = [];
    cashTransferQueue?: CashTransferQueueItem[] = [];
    cashRequestQueue?: CashRequestQueueItem[] = [];
    menuBarOpen?: boolean = false;
    selectedMenu?: SelectedMenu;
    selectedReport?: Report;
    queueMenuBarOpen?: boolean = false;
    selectedQueueMenu?: Queue;
    userQueue?: Array<Queue>;
    activeQueue?: QueueResponse;
    activeDepartmentId?: number | null;
    workWithKassa?: boolean;
}

const initialState = new HeaderReducer();

export default function header(state: HeaderReducer = initialState, action: HeaderAction) {
    switch (action.type) {
        case LOGIN_SUCCESS:
            return { ...state, userContext: action.payload, rolesData: getRoles(action.payload?.user?.roles || []), };
        case LOGOUT:
            return {};
        case OPEN_MENUBAR:
            return {
                ...state,
                menuBarOpen: action.payload
            };
        case SELECT_MENU:
            return {
                ...state,
                selectedMenu: action.payload
            };
        case SELECT_REPORT:
            return {
                ...state,
                selectedReport: action.payload
            };
        case OPEN_QUEUE_MENUBAR:
            return {
                ...state,
                queueMenuBarOpen: action.payload
            };
        case SELECT_QUEUE_MENU:
            return {
                ...state,
                selectedQueueMenu: action.payload
            };
        case UPDATE_QUEUE_MENU:
            return {
                ...state,
                selectedQueueMenu: (action.payload as Queue[]).find(q => q.id == state.selectedQueueMenu?.id)
            };
        case UPDATE_USER:
            return {
                ...state,
                userContext: {
                    ...state.userContext,
                    user: {
                        ...state.userContext?.user,
                        ...action.payload
                    }
                }
            };
        case UPDATE_USER_PHOTO:
            return {
                ...state,
                userContext: {
                    ...state.userContext,
                    user: {
                        ...state.userContext?.user,
                        photo: {
                            ...state.userContext?.user?.photo,
                            data: action.payload
                        }
                    }
                }
            };
        case UPDATE_USER_ROLES:
            return {
                ...state,
                userContext: {
                    ...state.userContext,
                    user: {
                        ...state.userContext?.user,
                        roles: action.payload
                    }
                }
            };
        case UPDATE_USER_QUEUE:
            return {
                ...state,
                userQueue: action.payload
            };
        case SELECT_ACTIVE_QUEUE:
            return {
                ...state,
                activeQueue: action.payload
            };
        case UPDATE_RETURN_REQUEST_QUEUE:
            return {
                ...state,
                acceptsQueue: action.payload
            };
        case UPDATE_CASHIER_REQUEST_QUEUE:
            return {
                ...state,
                cashierRequestQueue: action.payload
            };
        case UPDATE_CASH_TRANSFER_REQUEST_QUEUE:
            return {
                ...state,
                cashTransferQueue: action.payload
            };
        case UPDATE_CASH_REQUEST_QUEUE:
            return {
                ...state,
                cashRequestQueue: action.payload
            };
        case UPDATE_ACTIVE_DEPARTMENT_ID:
            return {
                ...state,
                activeDepartmentId: action.payload
            };
        case UPDATE_WORK_WITH_KASSA:
            return {
                ...state,
                workWithKassa: action.payload
            };
        default:
            return state;
    }
}

type HeaderAction =
    | ReturnType<typeof updateAcceptsQueue>
    | ReturnType<typeof updateCashierRequestQueue>
    | ReturnType<typeof updateCashTransferRequestQueue>
    | ReturnType<typeof updateCashRequestQueue>
    | ReturnType<typeof success>
    | ReturnType<typeof failure>
    | ReturnType<typeof logout>
    | ReturnType<typeof openMenuBar>
    | ReturnType<typeof selectMenu>
    | ReturnType<typeof selectReport>
    | ReturnType<typeof openQueueMenuBar>
    | ReturnType<typeof selectQueueMenu>
    | ReturnType<typeof updateQueueMenu>
    | ReturnType<typeof updateUser>
    | ReturnType<typeof updateUserRoles>
    | ReturnType<typeof updateUserQueue>
    | ReturnType<typeof selectActiveQueue>
    | ReturnType<typeof updateActiveDepartmentId>
    | ReturnType<typeof updateWorkWithKassa>
    | ReturnType<typeof updateUserPhoto>;

const updateAcceptsQueue = (acceptsQueue: AcceptQueueItem[]) => ({
    type: UPDATE_RETURN_REQUEST_QUEUE as typeof UPDATE_RETURN_REQUEST_QUEUE,
    payload: acceptsQueue
});

const updateCashierRequestQueue = (cashierRequestQueue: RequestQueueItem[]) => ({
    type: UPDATE_CASHIER_REQUEST_QUEUE as typeof UPDATE_CASHIER_REQUEST_QUEUE,
    payload: cashierRequestQueue
});

const updateCashTransferRequestQueue = (cashTransferQueue: CashTransferQueueItem[]) => ({
    type: UPDATE_CASH_TRANSFER_REQUEST_QUEUE as typeof UPDATE_CASH_TRANSFER_REQUEST_QUEUE,
    payload: cashTransferQueue
});

const updateCashRequestQueue = (cashRequestQueue: CashRequestQueueItem[]) => ({
    type: UPDATE_CASH_REQUEST_QUEUE as typeof UPDATE_CASH_REQUEST_QUEUE,
    payload: cashRequestQueue
});

const success = (userContext: UserContext) => ({
    type: LOGIN_SUCCESS as typeof LOGIN_SUCCESS,
    payload: userContext,
});

const failure = (error: string) => ({
    type: LOGIN_FAILURE as typeof LOGIN_FAILURE,
    payload: error,
});

const logout = () => ({
    type: LOGOUT as typeof LOGOUT,
    payload: null
});

const openMenuBar = (state: boolean) => ({
    type: OPEN_MENUBAR as typeof OPEN_MENUBAR,
    payload: state
});

const selectMenu = (menu: SelectedMenu) => ({
    type: SELECT_MENU as typeof SELECT_MENU,
    payload: menu
});

const selectReport = (report: Report) => ({
    type: SELECT_REPORT as typeof SELECT_REPORT,
    payload: report
});

const openQueueMenuBar = (state: boolean) => ({
    type: OPEN_QUEUE_MENUBAR as typeof OPEN_QUEUE_MENUBAR,
    payload: state
});

const selectQueueMenu = (menu?: Queue) => ({
    type: SELECT_QUEUE_MENU as typeof SELECT_QUEUE_MENU,
    payload: menu
});

const updateQueueMenu = (queues: Queue[]) => ({
    type: UPDATE_QUEUE_MENU as typeof UPDATE_QUEUE_MENU,
    payload: queues
});

const updateUser = (user: Partial<User>) => ({
    type: UPDATE_USER as typeof UPDATE_USER,
    payload: user
});

const updateUserPhoto = (photoBase64: string) => ({
    type: UPDATE_USER_PHOTO as typeof UPDATE_USER_PHOTO,
    payload: photoBase64
});

const updateUserRoles = (roles?: Role[]) => ({
    type: UPDATE_USER_ROLES as typeof UPDATE_USER_ROLES,
    payload: roles
});

const updateUserQueue = (userQueue?: Array<Queue>) => ({
    type: UPDATE_USER_QUEUE as typeof UPDATE_USER_QUEUE,
    payload: userQueue
});

const selectActiveQueue = (queue: QueueResponse) => ({
    type: SELECT_ACTIVE_QUEUE as typeof SELECT_ACTIVE_QUEUE,
    payload: queue
});

const updateActiveDepartmentId = (departmentId: number | string | null) => ({
    type: UPDATE_ACTIVE_DEPARTMENT_ID as typeof UPDATE_ACTIVE_DEPARTMENT_ID,
    payload: departmentId
});

const updateWorkWithKassa = (workWithKassa: boolean) => ({
    type: UPDATE_WORK_WITH_KASSA as typeof UPDATE_WORK_WITH_KASSA,
    payload: workWithKassa
});

export const actions = {
    success,
    failure,
    logout,
    updateUser,
    updateUserPhoto,
    updateUserRoles,
    openMenuBar,
    selectMenu,
    selectReport,
    openQueueMenuBar,
    selectQueueMenu,
    updateQueueMenu,
    updateUserQueue,
    selectActiveQueue,
    updateAcceptsQueue,
    updateCashierRequestQueue,
    updateCashTransferRequestQueue,
    updateCashRequestQueue,
    updateActiveDepartmentId,
    updateWorkWithKassa
};